import { useTranslation } from 'react-i18next'
import { Download } from 'lucide-react'
import clsx from 'clsx'

// 학부모용 브로셔(A4 세로 20쪽). 인쇄물 QR이 이 페이지를 가리키므로 경로(/brochure)는 고정한다.
// 개정판은 public/marketing/brochure/pages/ 이미지를 교체하고, PDF는 S3 quebon-static/brochure/에
// 날짜가 붙은 새 파일로 올린 뒤 아래 URL만 바꾼다(immutable 캐시라 같은 키 덮어쓰기 금지).
// CDN은 다른 출처라 <a download>가 무시되므로, 저장 파일명은 S3 객체의 Content-Disposition이 정한다.
const BROCHURE_PAGE_COUNT = 20
const BROCHURE_PAGE_WIDTH = 1400
const BROCHURE_PAGE_HEIGHT = 1979
const BROCHURE_PDF_URL = 'https://d2ql3aoebfyj3b.cloudfront.net/brochure/lc-parent-brochure-261002.pdf'
const BROCHURE_PDF_FILE_NAME = '깨봉수학-러닝센터-학부모-브로셔.pdf'
const EAGER_PAGE_COUNT = 2

const BROCHURE_PAGES = Array.from({ length: BROCHURE_PAGE_COUNT }, (_, index) => ({
  page: index + 1,
  src: `/marketing/brochure/pages/${String(index + 1).padStart(2, '0')}.webp`,
}))

interface DownloadPdfLinkProps {
  label: string
  size: 'sm' | 'lg'
}

function DownloadPdfLink({ label, size }: DownloadPdfLinkProps) {
  return (
    <a
      href={BROCHURE_PDF_URL}
      download={BROCHURE_PDF_FILE_NAME}
      className={clsx(
        'inline-flex shrink-0 items-center gap-2 rounded-full bg-qb-primary font-semibold text-qb-text-on-color transition-colors hover:bg-qb-primary-hover active:bg-qb-primary-pressed',
        size === 'sm' ? 'px-4 py-2 text-sm' : 'px-6 py-3 text-base',
      )}
    >
      <Download className="size-4" aria-hidden="true" />
      {label}
    </a>
  )
}

export default function BrochurePage() {
  const { t: mt } = useTranslation('marketing')

  return (
    <div className="bg-qb-bg-secondary">
      <div className="sticky top-16 z-30 border-b border-qb-divider bg-qb-bg/95 backdrop-blur-[12px]">
        <div className="mx-auto flex max-w-[840px] items-center justify-between gap-3 px-4 py-3">
          <h1 className="truncate text-base font-semibold text-qb-text-high md:text-lg">
            {mt('brochure.title')}
          </h1>
          <DownloadPdfLink label={mt('brochure.downloadPdf')} size="sm" />
        </div>
      </div>

      <div className="mx-auto flex max-w-[840px] flex-col gap-2 py-2 md:gap-6 md:px-4 md:py-8">
        {BROCHURE_PAGES.map(({ page, src }) => (
          <img
            key={page}
            src={src}
            width={BROCHURE_PAGE_WIDTH}
            height={BROCHURE_PAGE_HEIGHT}
            alt={mt('brochure.pageAlt', { page, total: BROCHURE_PAGE_COUNT })}
            loading={page <= EAGER_PAGE_COUNT ? 'eager' : 'lazy'}
            decoding="async"
            className="block h-auto w-full bg-qb-bg md:shadow-qb-card"
          />
        ))}
      </div>

      <div className="flex justify-center px-4 pb-12 pt-4 md:pb-20">
        <DownloadPdfLink label={mt('brochure.downloadPdf')} size="lg" />
      </div>
    </div>
  )
}
